/**
 * Phase 32 Plan 32-2 — leaderboard bar chart per UI-SPEC §242.
 *
 * Vertical (horizontal-bar) Recharts chart sourced from RESEARCH §793-820.
 * Bar fill switches to violet when `aiPct > 0.3` to surface AI-assisted
 * dominance at a glance (UI-SPEC §242). Clicking a bar invokes
 * `onSelect(userId)` for the parent page to navigate to the drill-down.
 *
 * Empty-state copy lives in this component (UI-SPEC §158) so the parent
 * page does not need to special-case zero-row results.
 */
import type { ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { LeaderboardRow } from '../../services/api/engineeringMetrics';

export interface LeaderboardBarChartProps {
  rows: LeaderboardRow[];
  onSelect: (userId: number) => void;
}

const COLOR_HUMAN = '#22c55e';
const COLOR_AI_DOMINANT = '#8b5cf6';

const numberFmt = new Intl.NumberFormat();

interface TooltipPayloadItem {
  payload?: LeaderboardRow;
  value?: number | string;
}

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}): ReactElement | null {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const aiPct = Math.round((row.aiPct ?? 0) * 100);
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm">
      <div className="font-medium text-gray-900">{row.userName}</div>
      <div className="text-gray-600">
        {row.total} {row.total === 1 ? 'commit' : 'commits'} ({aiPct}% AI-assisted)
      </div>
      <div className="text-gray-600">
        <span className="text-green-700">+{numberFmt.format(row.additions.total)}</span>
        {' / '}
        <span className="text-red-700">&#x2212;{numberFmt.format(row.deletions.total)}</span>
        {' lines, '}
        {numberFmt.format(row.filesChanged.total)} files
      </div>
    </div>
  );
}

export function LeaderboardBarChart({
  rows,
  onSelect,
}: LeaderboardBarChartProps): ReactElement {
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-base font-medium text-gray-900">No commits in this window</h3>
        <p className="mt-1 text-sm text-gray-500">
          Try widening the window or adjusting the project filter — the daily
          ingest may not have populated this slice yet.
        </p>
      </div>
    );
  }

  const height = Math.max(400, rows.length * 36);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="mb-3 flex items-center justify-end gap-4 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: COLOR_HUMAN }}
          />
          Mostly human (≤30% AI co-author)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: COLOR_AI_DOMINANT }}
          />
          AI-assisted dominant (&gt;30%)
        </span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 8, right: 24, left: 24, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 12, fill: '#6b7280' }} />
          <YAxis
            type="category"
            dataKey="userName"
            width={160}
            tick={{ fontSize: 12, fill: '#374151' }}
          />
          <Tooltip cursor={{ fill: '#f9fafb' }} content={<CustomTooltip />} />
          <Bar
            dataKey="total"
            cursor="pointer"
            onClick={(data) => {
              // recharts hands us a BarRectangleItem whose `.payload` is the
              // original LeaderboardRow we passed in via `data`.
              const payload = (data as { payload?: LeaderboardRow }).payload;
              if (payload && typeof payload.userId === 'number') {
                onSelect(payload.userId);
              }
            }}
            isAnimationActive={false}
          >
            {rows.map((r) => (
              <Cell
                key={r.userId}
                fill={r.aiPct > 0.3 ? COLOR_AI_DOMINANT : COLOR_HUMAN}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default LeaderboardBarChart;
